import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import type { FilterField, Option } from '../../interfaces/Dashboard';

interface RequestFilterBarProps {
  fields: FilterField[];
  isLoading: boolean;
  onFieldChange: (responseValue: string, value: string | Option | null) => void;
  onSearch: () => void;
  onClear: () => void;
}

/**
 * Dashboard search bar. Controls are driven by the `filterFields` config rather
 * than hardcoded, so adding a filter is a constants change — the same
 * config-driven approach the legacy `fetchSearchFieldsTemplate` used.
 */
const RequestFilterBar = ({
  fields,
  isLoading,
  onFieldChange,
  onSearch,
  onClear,
}: RequestFilterBarProps) => {
  const renderField = (field: FilterField) => {
    const helperText = field.isError ? field.errorMessage : '';

    switch (field.type) {
      case 'string':
      case 'integer':
        return (
          <TextField
            label={field.label}
            fullWidth
            name={field.id}
            id={field.id}
            type={field.type === 'integer' ? 'number' : 'text'}
            value={(field.value as string) ?? ''}
            onChange={(event) =>
              onFieldChange(field.responseValue, event.target.value)
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSearch();
            }}
            helperText={helperText}
            error={field.isError}
          />
        );

      case 'select':
        return field.isMultiple ? (
          <Autocomplete
            multiple
            options={field.options ?? []}
            value={(field.value as unknown as Option[]) ?? []}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            filterSelectedOptions
            blurOnSelect
            size='small'
            onChange={(_event, value) =>
              onFieldChange(field.responseValue, value as unknown as Option)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                helperText={helperText}
                error={field.isError}
              />
            )}
            className='autocomplete-field'
          />
        ) : (
          <Autocomplete
            options={field.options ?? []}
            value={(field.value as Option) ?? null}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            blurOnSelect
            size='small'
            onChange={(_event, value) =>
              onFieldChange(field.responseValue, value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                helperText={helperText}
                error={field.isError}
              />
            )}
            className='autocomplete-field'
          />
        );

      case 'date':
      case 'datetime':
        return (
          <TextField
            id={field.id}
            label={field.label}
            type={field.type === 'date' ? 'date' : 'datetime-local'}
            value={(field.value as string) ?? ''}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) =>
              onFieldChange(field.responseValue, event.target.value)
            }
            helperText={helperText}
            error={field.isError}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Toolbar className='search-requests-wrapper'>
      <Grid container spacing={3}>
        {fields.map((field) => (
          <Grid key={field.id} size={{ xs: field.xs, md: field.md }}>
            {renderField(field)}
          </Grid>
        ))}

        <Grid
          size={{ xs: 12, md: 10 }}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            variant='outlined'
            color='primary'
            onClick={onClear}
            disabled={isLoading}
            title='Clear'
            sx={{ marginRight: '1rem', minWidth: '45px' }}
          >
            Clear
          </Button>

          <Button
            variant='contained'
            color='primary'
            onClick={onSearch}
            disabled={isLoading}
            title='Search'
            sx={{ minWidth: '45px' }}
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Toolbar>
  );
};

export default RequestFilterBar;
